import Form from "@/components/Form";
import PageHeader from "@/components/PageHeader";
import SaveButton from "@/components/SaveButton";
import Spinner from "@/components/Spinner";
import Title from "@/components/Title";

import Field from "@/components/Field";
import RecordView from "@/components/record/RecordView";
import { TextareaWithLimitInfo } from "@/components/TextareaWithLimitInfo";
import { Module, PATH_PAYROLL_SPLIT } from "@/constants";
import { updatePayrollSplits } from "@/schemas/payrollSplit";
import { PayrollsService } from "@/services/payrolls";
import { PayrollSplit } from "@/types/payrollSplit";
import { z } from "@/zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { memo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router-dom";
import { usePayrollBreadcrumbs } from "../hooks/usePayrollBreadcrumbs";
import { usePayrollParams } from "../hooks/usePayrollParams";
import { usePayrollPermissions } from "../hooks/usePayrollPermissions";
import { usePayrollQuery } from "../hooks/usePayrollQuery";
import { useUpdatePayrollSplitDetailMutation } from "../hooks/useUpdatePayrollSplitDetailMutation";
import { useValueRenderer } from "../../../hooks/useValueRenderer";
import { updatePayrollSplitDetailsFormSchema } from "../schema";
import { PayrollSplitRecordView } from "./PayrollSplitRecordView";
import { NumberInput } from "@/components/ui/NumberInput";
import { formatDateTime, formatNumber } from "@/lib/utils/formatters";

type FormData = z.infer<typeof updatePayrollSplits>;

const defaultValues = {
  amount_to_pay: null,
  amount_agreement1: null,
  contest1: null,
  contest2: null,
  contest3: null,
  contest4: null,
  contest5: null,
  contest6: null,
  contest7: null,
  amount_agreement2: null,
  comment: null,
};

export const PayrollSplitDetail = memo(() => {
  const { t } = useTranslation();
  const { payrollId, entryId } = usePayrollParams();
  const payrollQuery = usePayrollQuery(payrollId);
  const permissions = usePayrollPermissions();
  const mutation = useUpdatePayrollSplitDetailMutation();

  const breadcrumbs = usePayrollBreadcrumbs([
    {
      label: t("payrolls.views.split.title"),
      href: generatePath(PATH_PAYROLL_SPLIT, {
        payrollId,
      }),
    },
    {
      label: `${t("payrolls.details.title")}`,
    },
  ]);

  const entryQuery = useQuery<PayrollSplit>({
    queryKey: [Module.Payrolls, payrollId, "split", entryId],
    queryFn: () => PayrollsService.getSplit(entryId),
    enabled: payrollQuery.isSuccess,
  });

  const form = useForm<FormData>({
    resolver: zodResolver(updatePayrollSplitDetailsFormSchema),
    defaultValues,
    disabled:
      !permissions.can_update ||
      !permissions.isInSplitStatus ||
      mutation.isPending ||
      entryQuery.isFetching,
  });

  useEffect(() => {
    if (entryQuery.data === undefined) {
      return;
    }
    const { id, ...rest } = entryQuery?.data || {};
    form.reset(rest);
  }, [entryQuery.data, form]);

  const handleSubmit = useCallback(
    (values: FormData) => {
      return mutation.mutateAsync(values);
    },
    [mutation],
  );

  const renderValue = useValueRenderer();

  const entry = entryQuery.data;

  if (payrollQuery.isLoading || entryQuery.isLoading) {
    return <Spinner className="w-full h-full" />;
  }

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbs} />

      <Title>{t("payrolls.views.split.title")}</Title>

      <PayrollSplitRecordView />

      <Title level={4}>{t("payrolls.details.title")}</Title>

      <Form form={form} handleSubmit={handleSubmit}>
        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.basic"
          header={() => t("payrolls.fieldGroups.basic")}
          isOpen={true}
        >
          <RecordView.Field
            name="period"
            label={t("payrolls.fields.period")}
            renderer={() => renderValue(entry?.period)}
          />
          <RecordView.Field
            name="contractor"
            label={t("payrolls.fields.contractor")}
            renderer={() => renderValue(entry?.contractor)}
          />
          <RecordView.Field
            name="agreement_company"
            label={t("payrolls.fields.agreement_company")}
            renderer={() => renderValue(entry?.agreement_company)}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.contractor"
          header={() => t("payrolls.fieldGroups.contractor")}
          isOpen={true}
        >
          <RecordView.Field
            name="personal_id_number"
            label={t("payrolls.fields.personal_id_number")}
            renderer={() => renderValue(entry?.personal_id_number)}
          />
          <RecordView.Field
            name="tax_status_final"
            label={t("payrolls.fields.tax_status_final")}
            renderer={() => renderValue(entry?.tax_status_final, "taxStatus")}
          />
          <RecordView.Field
            name="citizenship"
            label={t("payrolls.fields.citizenship")}
            renderer={() => renderValue(entry?.citizenship)}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.financial"
          header={() => t("payrolls.fieldGroups.financial")}
          isOpen={true}
        >
          <Field
            name="amount_to_pay"
            label={t("payrolls.fields.amount_to_pay")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <RecordView.Field
            name="total_contests"
            label={t("payrolls.fields.total_contests")}
            renderer={() => formatNumber(entry?.total_contests)}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.agreements"
          header={() => t("payrolls.fieldGroups.agreements")}
          isOpen={true}
        >
          <Field
            name="amount_agreement1"
            label={t("payrolls.fields.amount_agreement1")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="contest1"
            label={t("payrolls.fields.contest1")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="contest2"
            label={t("payrolls.fields.contest2")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="contest3"
            label={t("payrolls.fields.contest3")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="contest4"
            label={t("payrolls.fields.contest4")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="contest5"
            label={t("payrolls.fields.contest5")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="contest6"
            label={t("payrolls.fields.contest6")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="contest7"
            label={t("payrolls.fields.contest7")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="amount_agreement2"
            label={t("payrolls.fields.amount_agreement2")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <RecordView.Field
            name="block_agreement2"
            label={t("payrolls.fields.block_agreement2")}
            renderer={() =>
              renderValue(entry?.block_agreement2, "booleanValues")
            }
          />
          <RecordView.Field
            name="block_contests"
            label={t("payrolls.fields.block_contests")}
            renderer={() => renderValue(entry?.block_contests, "booleanValues")}
          />
          <RecordView.Field
            name="contest_survey_uploaded"
            label={t("payrolls.fields.contest_survey_uploaded")}
            renderer={() =>
              renderValue(entry?.contest_survey_uploaded, "booleanValues")
            }
          />
          <RecordView.Field
            name="agreement2_sent_at"
            label={t("payrolls.fields.agreement2_sent_at")}
            renderer={() => formatDateTime(entry?.agreement2_sent_at)}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.comments"
          header={() => t("payrolls.fieldGroups.comments")}
          isOpen={true}
        >
          <Field
            name="comment"
            label={t("payrolls.fields.comment_split")}
            render={({ field }) => (
              <TextareaWithLimitInfo
                {...field}
                placeholder={t("payrolls.placeholders.comment")}
                maxLength={250}
              />
            )}
          />
          <RecordView.Field
            name="permanent_comment"
            label={t("payrolls.fields.permanent_comment")}
            renderer={() => renderValue(entry?.permanent_comment)}
          />
        </RecordView>

        <footer className="flex py-2 justify-end gap-6 mt-6">
          <SaveButton />
        </footer>
      </Form>
    </>
  );
});

PayrollSplitDetail.displayName = "PayrollSplitDetail";
